import {
  CardBtnArrowIcon,
  CardButton,
  GridItem,
  TypographyV3,
} from '@jouzen/ecom-components';
import { cx } from 'class-variance-authority';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import type { MouseEvent, PropsWithChildren } from 'react';

import { EventType } from '@/analytics/types';
import BackdropBlur from '@/app/components/BackdropBlur';
import Image from '@/app/components/Image';
import RichText from '@/app/components/RichText';

import type { Slide } from './_data/sliderContent';

interface WhyOuraSliderItemProps {
  readonly slide: Slide;
}

const SlideItem = ({ slide }: WhyOuraSliderItemProps) => {
  const Icon = slide.tag.icon;
  const t = useTranslations();
  return (
    <>
      <GridItem
        className="relative"
        colEnd={{ sm: 3 }}
        colStart={{ sm: 1 }}
        rowEnd={{ sm: 3 }}
        rowStart={{ sm: 1 }}
      >
        <Image
          className="rounded-lg object-cover"
          draggable={false}
          alt={t(slide.image.alt)}
          src={slide.image.src}
          fill
          sizes="(max-width: 768px) 90vw, 35vw"
        />
      </GridItem>
      <GridItem
        className="pt-6 pl-6"
        colStart={{ sm: 1 }}
        colEnd={{ sm: 3 }}
        rowEnd={{ sm: 2 }}
        rowStart={{ sm: 1 }}
      >
        <div
          className={cx(
            'relative inline-flex items-center gap-x-2.5 overflow-hidden',
            'px-4 py-3 md:px-6 md:py-4',
          )}
        >
          <BackdropBlur variant="pill" />
          <Icon className="relative inline-block w-6 text-sandstone-200" />
          <TypographyV3
            Element="span"
            color="light"
            variant="eyebrow-small"
            className="relative"
          >
            {t(slide.tag.label)}
          </TypographyV3>
        </div>
      </GridItem>
    </>
  );
};

type BottomRowProps = PropsWithChildren<WhyOuraSliderItemProps>;

const BottomRow = ({ children, slide }: BottomRowProps) => {
  return (
    <GridItem
      className="relative flex justify-between gap-4 self-end px-6 pb-6"
      colStart={{ sm: 1 }}
      colEnd={{ sm: 3 }}
      rowEnd={{ sm: 3 }}
      rowStart={{ sm: 2 }}
    >
      <TypographyV3
        className="max-w-[325px]"
        key={slide.id}
        Element="h3"
        variant="h4"
        color="light"
        height="none"
      >
        <RichText messageKey={slide.title} />
      </TypographyV3>
      {children}
    </GridItem>
  );
};

const WhyOuraSliderItem = ({ slide }: WhyOuraSliderItemProps) => {
  const t = useTranslations();
  const tagLabel = t(slide.tag.label);
  const handleLinkClick = async (
    event: MouseEvent<HTMLAnchorElement>,
  ): Promise<void> => {
    const { href } = event.currentTarget;
    await window.ouraAnalytics.track(EventType.LinkClicked, {
      cta: slide.category,
      location: 'body',
      path: href,
    });
  };
  return (
    <div data-cy="why-oura-slider-item">
      <Link
        className="mb-8 hidden h-[370px] w-[493px] grid-cols-2 grid-rows-2 lg:grid"
        data-cy="learn-more-button"
        href={slide.button.href}
        locale="en"
        onClick={handleLinkClick}
        rel="noopener noreferrer"
        target={slide.button.target}
      >
        <SlideItem slide={slide} />
        <BottomRow slide={slide}>
          <div className="hidden self-end rounded-full bg-sandstone-200 p-4 text-center text-sandstone-500 lg:inline-block">
            <CardBtnArrowIcon aria-hidden="true" className="-rotate-45" />
          </div>
        </BottomRow>
      </Link>
      <div className="mb-8 grid h-[370px] w-[80vw] grid-cols-2 grid-rows-2 md:w-[493px] lg:hidden">
        <SlideItem slide={slide} />
        <BottomRow slide={slide}>
          <CardButton
            ariaLabel={t('learn_more_aria_label', { tag: tagLabel })}
            className="self-end lg:hidden"
            color="light"
            external
            href={slide.button.href}
            onClick={handleLinkClick}
            rel="noopener noreferrer"
            target="_blank"
            variant="arrow"
          />
        </BottomRow>
      </div>
    </div>
  );
};

export default WhyOuraSliderItem;
